/**
 * No-show Protection settings page (Calendar & Appointments). Stripe-hosted onboarding returns here with
 * `?stripe=return|refresh`; the page handles it once and drops the param.
 */

import { useCallback } from 'react'
import { createFileRoute, useParams } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'
import { NoShowProtectionSettingsPage } from '@/features/no-show-protection/settings/no-show-protection-settings-page'
import type { StripeReturnParam } from '@/features/no-show-protection/settings/use-stripe-connect-flow'
import { BusinessSetupPageShell } from './business-setup/components/business-setup-page-shell'
import { formatDocumentTitle } from '@/lib/document-title'

interface NoShowProtectionSearch {
  stripe?: StripeReturnParam
}

export const Route = createFileRoute('/_auth/$orgId/settings/calendar-appointments/no-show-protection')({
  validateSearch: (search: Record<string, unknown>): NoShowProtectionSearch => ({
    stripe: search.stripe === 'return' || search.stripe === 'refresh' ? search.stripe : undefined,
  }),
  component: NoShowProtectionRoute,
})

function NoShowProtectionRoute() {
  const { orgId } = useParams({ from: '/_auth/$orgId/settings/calendar-appointments/no-show-protection' })
  const { stripe } = Route.useSearch()
  const navigate = Route.useNavigate()
  const clearStripeParam = useCallback(() => {
    void navigate({ search: (prev) => ({ ...prev, stripe: undefined }), replace: true })
  }, [navigate])

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('No-show Protection', 'Calendar & Appointments')}</title>
      </Helmet>

      <BusinessSetupPageShell orgId={orgId} activePath="no-show-protection">
        {/* The route guard opens this page only with Calendar and check-in on (no-shows need both). */}
        <NoShowProtectionSettingsPage orgId={orgId} stripeParam={stripe} onStripeParamHandled={clearStripeParam} />
      </BusinessSetupPageShell>
    </>
  )
}
